import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { CustomLabel, FieldErrorMessage } from "@/components/atoms";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Item = {
  id: string;
  name: string;
  disabled?: boolean;
};

interface SearchableSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  items: Item[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isSearchLoading?: boolean;
  emptyMessage?: string;
}

const SearchableSelect = ({
  value,
  onChange,
  items,
  placeholder = "Select...",
  className,
  disabled,
  hasError = false,
  searchValue,
  onSearchChange,
  isSearchLoading = false,
  emptyMessage = "Not found",
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState("");
  const effectiveSearchValue = searchValue ?? localSearchValue;
  const isRemoteSearch = Boolean(onSearchChange);
  const selectedItem = items.find(item => item.id === value);

  const displayedItems = useMemo(() => {
    if (isRemoteSearch) return items;

    const normalizedSearch = effectiveSearchValue.trim().toLowerCase();
    if (!normalizedSearch) return items;

    return items.filter(item =>
      item.name.toLowerCase().includes(normalizedSearch)
    );
  }, [effectiveSearchValue, isRemoteSearch, items]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setLocalSearchValue("");
      onSearchChange?.("");
    }
  };

  const handleSearchChange = (nextSearchValue: string) => {
    setLocalSearchValue(nextSearchValue);
    onSearchChange?.(nextSearchValue);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-10 min-w-0 w-full items-center justify-between gap-2 rounded border bg-white px-3 text-sm transition-all disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50",
            hasError && "border-red-500",
            className
          )}
        >
          <span
            className={cn(
              "truncate text-left",
              !selectedItem && "text-gray-400"
            )}
          >
            {selectedItem?.name ?? placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-30" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            placeholder="Search..."
            value={effectiveSearchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            {isSearchLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">
                Searching...
              </div>
            ) : displayedItems.length > 0 ? (
              <CommandGroup>
                {displayedItems.map(item => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    disabled={item.disabled}
                    onSelect={() => {
                      onChange?.(item.id);
                      handleOpenChange(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4",
                        item.id === value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{item.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <div className="py-6 text-center text-sm text-gray-500">
                {emptyMessage}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface SearchableSelectControlProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  items: Item[];
  label?: string | React.ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isSearchLoading?: boolean;
  emptyMessage?: string;
  onValueChange?: (value: string) => void;
}

const SearchableSelectControl = <T extends FieldValues>({
  form,
  name,
  items,
  label,
  placeholder,
  className,
  disabled,
  searchValue,
  onSearchChange,
  isSearchLoading,
  emptyMessage,
  onValueChange,
}: SearchableSelectControlProps<T>) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field, fieldState }) => (
      <FormItem className="relative min-w-0">
        {label && <CustomLabel htmlFor={name} label={label} />}
        <FormControl>
          <SearchableSelect
            value={field.value ?? ""}
            onChange={nextValue => {
              field.onChange(nextValue);
              onValueChange?.(nextValue);
            }}
            items={items}
            placeholder={placeholder}
            className={className}
            disabled={disabled}
            hasError={!!fieldState.error}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            isSearchLoading={isSearchLoading}
            emptyMessage={emptyMessage}
          />
        </FormControl>
        <FieldErrorMessage />
      </FormItem>
    )}
  />
);

export default SearchableSelectControl;
